import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ActivityCard } from './components';
import { ViewToggle } from '../shared';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { Activity, ActivityKind } from './activitiesTypes';
import { useSalesActivitiesStore } from '../../../store/salesActivitiesStore';
import { SalesActivity, ActivityKind as BackendKind } from '../../../services/salesActivitiesApi';
import styles from './SalesRepActivities.module.css';

const filterPills = [
  'All', 'Call', 'Visit', 'Meeting', 'Note',
  'Follow-Up', 'Pricing', 'Collection', 'New Account', 'Display', 'Other',
] as const;

const kindLabel: Record<BackendKind, ActivityKind> = {
  call: 'Call',
  visit: 'Visit',
  meeting: 'Meeting',
  note: 'Note',
  follow_up: 'Follow-Up',
  pricing: 'Pricing',
  collection: 'Collection',
  new_account: 'New Account',
  display: 'Display',
  other: 'Other',
};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + `, ${time}`;
}

function adapt(a: SalesActivity): Activity {
  return {
    id: String(a.id),
    kind: kindLabel[a.kind],
    customer: a.customer_name ?? 'No customer',
    title: a.subject,
    notes: a.notes ?? '',
    timestamp: formatTimestamp(a.occurred_at),
    followUp: a.follow_up_at
      ? new Date(a.follow_up_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
      : undefined,
    priority: a.priority,
    completed: !!a.completed_at,
  };
}

const SalesRepActivities: React.FC = () => {
  const items = useSalesActivitiesStore((s) => s.items);
  const loading = useSalesActivitiesStore((s) => s.loading);
  const error = useSalesActivitiesStore((s) => s.error);
  const fetchList = useSalesActivitiesStore((s) => s.fetchList);
  const updateActivity = useSalesActivitiesStore((s) => s.update);

  const [filter, setFilter] = useState<typeof filterPills[number]>('All');
  const [view, setView] = useState<'card' | 'grid'>('card');

  useEffect(() => { fetchList(); }, [fetchList]);

  const activities = useMemo<Activity[]>(() => items.map(adapt), [items]);
  const filtered = filter === 'All' ? activities : activities.filter((a) => a.kind === filter);

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>Activity Timeline</h2>
          <p className={styles.subtitle}>{activities.length} activities</p>
        </div>
        <Link to="/log-activity" className={styles.logBtn}>+ Log Activity</Link>
      </div>

      <div className={styles.filters}>
        <div className={styles.pillRow}>
          {filterPills.map((pill) => (
            <button
              key={pill}
              className={`${styles.pill} ${filter === pill ? styles.pillActive : ''}`}
              onClick={() => setFilter(pill)}
            >
              {pill}
            </button>
          ))}
        </div>
        <ViewToggle value={view} onChange={setView} />
      </div>

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && filtered.length === 0}
        emptyMessage="No activities yet — log your first one"
        onRetry={() => fetchList()}
      >
        <div className={styles.list}>
          {filtered.map((a) => (
            <ActivityCard
              key={a.id}
              activity={a}
              onMarkComplete={(id) => updateActivity(Number(id), { completed_at: new Date().toISOString() })}
            />
          ))}
        </div>
      </AsyncBoundary>
    </div>
  );
};

export const SalesRepActivitiesPage: React.FC = () => {
  return <SalesRepActivities />;
};

export { SalesRepActivities };
export default SalesRepActivities;
